import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { siteInfo, testimonials, faqs } from '@/mocks/anime';

export default function About() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setFormSubmitting(true);

    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormSubmitted(true);
      setFormSubmitting(false);
      return;
    }

    try {
      const formData = new FormData(form);
      if (honeypotField) formData.delete('website_alt');

      const response = await fetch('https://readdy.ai/api/form/d9omteiqs38sj6jc7blg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后重试。');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试。');
        }
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试。');
    } finally {
      setFormSubmitting(false);
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'AboutPage',
        name: `关于51动漫 - ${siteInfo.name}`,
        description: `${siteInfo.name}是专业的免费在线动漫平台，提供海量日漫韩漫国漫资源。了解我们的品牌故事、用户口碑与联系方式。`,
        url: 'https://www.51dongman.com/about',
      },
      {
        '@type': 'Organization',
        name: siteInfo.name,
        url: 'https://www.51dongman.com',
        description: siteInfo.description,
        email: siteInfo.email,
        telephone: siteInfo.phone,
        address: {
          '@type': 'PostalAddress',
          streetAddress: siteInfo.address,
          addressCountry: 'CN',
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.51dongman.com' },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <main>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <Navbar />
        <div className="pt-16">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          <section className="py-10 md:py-16 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto text-center">
              <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 mb-6">关于{siteInfo.name}</h1>
              <p className="text-foreground-600 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
                {siteInfo.name}成立于2020年，是国内领先的免费在线动漫阅读平台。我们致力于为广大动漫爱好者提供最全、最新、最高清的动漫资源，
                涵盖<strong>日本动漫</strong>、<strong>韩国漫画</strong>、<strong>国产动漫</strong>等多个分类，
                支持<strong>下拉式在线阅读</strong>，让追漫体验更加流畅舒适。
              </p>
            </div>
          </section>

          <section className="py-10 md:py-14 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2 text-center">品牌优势</h2>
              <p className="text-foreground-600 text-sm text-center mb-10">为什么选择51动漫</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {[
                  { icon: 'ri-shield-check-line', title: '正版授权', desc: '与多家动漫制作方合作，提供正版授权内容，画质清晰流畅。' },
                  { icon: 'ri-flashlight-line', title: '极速更新', desc: '热门动漫播出后2-4小时内同步更新，让您第一时间追更。' },
                  { icon: 'ri-device-line', title: '全平台支持', desc: 'PC端、平板端、手机端全面适配，随时随地想看就看。' },
                  { icon: 'ri-emotion-happy-line', title: '完全免费', desc: '承诺永久免费，无需充值会员，所有动漫内容畅享无阻。' },
                ].map((item, i) => (
                  <div key={i} className="text-center p-5 rounded-xl bg-background-50 border border-background-200/70">
                    <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                      <i className={`${item.icon} text-xl text-primary-600`}></i>
                    </div>
                    <h3 className="font-heading text-lg text-foreground-950 mb-2">{item.title}</h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2 text-center">用户口碑</h2>
              <p className="text-foreground-600 text-sm text-center mb-10">听听动漫爱好者们怎么说</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                {testimonials.map((t) => (
                  <div key={t.id} className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                        <span className="text-accent-700 font-heading text-sm">{t.avatar}</span>
                      </div>
                      <div>
                        <p className="text-foreground-950 font-medium text-sm">{t.name}</p>
                        <div className="flex items-center gap-0.5">
                          {Array.from({ length: t.rating }).map((_, i) => (
                            <i key={i} className="ri-star-fill text-xs text-accent-400"></i>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-foreground-600 text-sm leading-relaxed">{t.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2 text-center">常见问题</h2>
              <p className="text-foreground-600 text-sm text-center mb-10">您关心的问题都在这里</p>
              <div className="space-y-3">
                {faqs.map((faq, i) => (
                  <div key={i} className="rounded-xl bg-background-50 border border-background-200/70 overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                    >
                      <span className="text-foreground-950 font-medium text-sm">{faq.q}</span>
                      <i className={`ri-${openFaq === i ? 'subtract' : 'add'}-line text-foreground-500 transition-transform`}></i>
                    </button>
                    {openFaq === i && (
                      <div className="px-5 pb-4">
                        <p className="text-foreground-600 text-sm leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2 text-center">联系我们</h2>
              <p className="text-foreground-600 text-sm text-center mb-10">有任何问题或建议，欢迎随时联系我们</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-lg text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-foreground-950 font-medium text-sm">电子邮箱</p>
                      <p className="text-foreground-600 text-sm">{siteInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-foreground-950 font-medium text-sm">客服电话</p>
                      <p className="text-foreground-600 text-sm">{siteInfo.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-lg text-secondary-600"></i>
                    </div>
                    <div>
                      <p className="text-foreground-950 font-medium text-sm">公司地址</p>
                      <p className="text-foreground-600 text-sm">{siteInfo.address}</p>
                    </div>
                  </div>
                </div>

                <div>
                  {formSubmitted ? (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                        <i className="ri-check-line text-xl text-green-600"></i>
                      </div>
                      <p className="text-foreground-950 font-medium">感谢您的留言！</p>
                      <p className="text-foreground-600 text-sm mt-1">我们会尽快回复您。</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
                      {formError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                          {formError}
                        </div>
                      )}
                      <input
                        type="text"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="honeypot-field"
                      />
                      <div>
                        <label className="block text-foreground-700 text-sm font-medium mb-1.5">姓名</label>
                        <input
                          type="text"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:border-primary-300 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label className="block text-foreground-700 text-sm font-medium mb-1.5">邮箱</label>
                        <input
                          type="email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:border-primary-300 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label className="block text-foreground-700 text-sm font-medium mb-1.5">留言内容</label>
                        <textarea
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:border-primary-300 transition-colors resize-none"
                          placeholder="请输入您的留言内容（最多500字）"
                        ></textarea>
                      </div>
                      <button
                        type="submit"
                        disabled={formSubmitting}
                        className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-background-50 font-medium rounded-full text-sm transition-all cursor-pointer whitespace-nowrap"
                      >
                        {formSubmitting ? '提交中...' : '发送留言'}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
        <Footer />
      </main>
    </>
  );
}